import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {deleteCard} from 'common/src/modules/campaigns/store/campaignLogicTab/actions/deleteCard';
import toConnectedMapNode from 'common/src/modules/campaigns/store/campaignLogicTab/utils/toConnectedMapNode';
import {DisconnectedMapNode} from 'common/src/modules/nodes/nodes.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../campaignSetupAgent.enums';
import refreshCampaignIssues from '../../../utils/refreshCampaignIssues';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const deleteCardToolSchema = z
  .object({
    cardId: z.string().nullable().optional().describe(describeIdFormat(IdPrefix.Card)),
    cardName: z.string().nullable().optional().describe('The name of the card to delete'),
  })
  .refine((data) => data.cardId !== undefined || data.cardName !== undefined, {
    message: 'Either cardId or cardName must be provided',
    path: ['cardId', 'cardName'],
  });

function deleteCardTool(store: CLStore) {
  async function execute({
    input: {cardId, cardName},
  }: ToolExecuteParams<typeof deleteCardToolSchema>): Promise<ToolResult> {
    try {
      const state = store.getState();
      const nodes = state.campaignPage.campaignLogic.nodes[CampaignTreeType.Campaign];

      if (!nodes) {
        return toolError(
          `No nodes found for campaign tree type ${CampaignTreeType.Campaign}. Make sure you're in a campaign context.`,
        );
      }

      let nodeToDelete;
      let nodeIdToDelete: number | undefined;

      if (cardId != null) {
        const parseResult = idHelper.parseCardId(cardId);

        if (parseResult.ok === false) {
          return toolError(formatParseError(parseResult.error));
        }

        nodeIdToDelete = parseResult.numericId;
        nodeToDelete = nodes[nodeIdToDelete];

        if (!nodeToDelete) {
          return toolError(
            `Card ${idHelper.toCardId(nodeIdToDelete)} not found. Available card IDs: ${Object.keys(
              nodes,
            )
              .map((id) => idHelper.toCardId(Number(id)))
              .join(', ')}`,
          );
        }
      } else if (cardName !== undefined) {
        const matchingEntries = Object.entries(nodes).filter(
          ([_, node]) =>
            node.name?.toLowerCase().includes(cardName.toLowerCase()) ||
            node.description?.toLowerCase().includes(cardName.toLowerCase()),
        );

        if (matchingEntries.length === 0) {
          const availableNames = Object.values(nodes)
            .map((node) => node.name || node.description)
            .filter(Boolean)
            .join(', ');

          return toolError(
            `No card found with name containing "${cardName}". Available cards: ${availableNames}`,
          );
        }

        if (matchingEntries.length > 1) {
          const matchingNames = matchingEntries
            .map(
              ([id, node]) =>
                `ID: ${idHelper.toCardId(Number(id))}, Name: "${node.name || node.description}"`,
            )
            .join('; ');

          return toolError(
            `Multiple cards found matching "${cardName}". Please be more specific or use card ID. Matches: ${matchingNames}`,
          );
        }

        const [rawId, matchedNode] = matchingEntries[0];
        nodeIdToDelete = parseInt(rawId);
        nodeToDelete = matchedNode;
      }

      if (!nodeToDelete) {
        return toolError(
          `No card found with name containing "${cardName}". Available cards: ${Object.keys(nodes)
            .map((id) => idHelper.toCardId(Number(id)))
            .join(', ')}`,
        );
      }

      const connectedNodeToDelete = toConnectedMapNode(nodeToDelete as DisconnectedMapNode);

      const result = await store.dispatch(
        deleteCard({
          nodeToDelete: connectedNodeToDelete,
          campaignTreeType: CampaignTreeType.Campaign,
        }),
      );

      if (deleteCard.fulfilled.match(result)) {
        await refreshCampaignIssues(store);

        const cardIdentifier =
          nodeToDelete.name || nodeToDelete.description || idHelper.toCardId(nodeToDelete.id);

        return toolResult(
          `Successfully deleted card "${cardIdentifier}" from the campaign setup. Any connections to other cards have been automatically updated.`,
        );
      } else {
        const errorMessage = result.error?.message || 'Unknown error occurred';

        return toolError(`Failed to delete card: ${errorMessage}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      return toolError(`Error deleting card: ${errorMessage}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.DeleteCard,
    displayName: 'Deleting Campaign Card',
    description:
      'Deletes a card from the campaign setup by card ID or name. Automatically handles updating connections to other cards when the card is removed.',
    execute,
    inputSchema: deleteCardToolSchema,
  });
}

export default deleteCardTool;
